/**
 * Danger Zone — GDPR data rights (v2.5.897)
 *
 * Two flows exposed here:
 *   1. Right to Access — one-click JSON export of every tenant-scoped record.
 *   2. Right to be Forgotten — schedules deletion with a 30-day grace window.
 *
 * Deletion is intentionally 2-step: the user must type their exact business
 * name to confirm, then can still cancel from the same panel until purge day.
 */
import { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { AlertTriangle, Download, Loader2, Shield, XCircle } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function DangerZoneTab() {
  const { user } = useAuth();
  const [exporting, setExporting] = useState(false);
  const [scheduling, setScheduling] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [status, setStatus] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [reason, setReason] = useState('');

  const businessName = user?.business_name || user?.email || '';

  const fetchStatus = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/gdpr/deletion-status`);
      setStatus(data);
    } catch {
      /* silent — tab may render before session is fully hydrated */
    }
  };

  useEffect(() => { fetchStatus(); }, []);

  const handleExport = async () => {
    setExporting(true);
    try {
      const { data } = await axios.get(`${API_URL}/api/gdpr/export`);
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `konnect-data-export-${new Date().toISOString().slice(0, 10)}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success(`Exported ${Object.values(data.summary || {}).reduce((s, n) => s + n, 0)} records across ${Object.keys(data.summary || {}).length} collections.`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Export failed');
    } finally {
      setExporting(false);
    }
  };

  const handleScheduleDelete = async () => {
    if (confirmText.trim().toLowerCase() !== businessName.trim().toLowerCase()) {
      toast.error(`Please type "${businessName}" exactly to confirm.`);
      return;
    }
    setScheduling(true);
    try {
      const { data } = await axios.post(`${API_URL}/api/gdpr/delete-request`, {
        confirm_text: confirmText,
        reason: reason.trim(),
      });
      toast.success(data.message || 'Deletion scheduled');
      setConfirmOpen(false);
      setConfirmText('');
      setReason('');
      fetchStatus();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not schedule deletion');
    } finally {
      setScheduling(false);
    }
  };

  const handleCancelDeletion = async () => {
    setCancelling(true);
    try {
      await axios.post(`${API_URL}/api/gdpr/cancel-deletion`);
      toast.success('Deletion request cancelled. Your data is safe.');
      fetchStatus();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not cancel');
    } finally {
      setCancelling(false);
    }
  };

  const hasPending = status?.status === 'pending';

  return (
    <div className="space-y-6" data-testid="danger-zone-tab">
      {/* Right to Access — data export */}
      <Card className="border-blue-200">
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-heading flex items-center gap-2">
            <Download className="h-4 w-4 text-blue-600" />
            Download Your Data
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-sm text-gray-medium">
            Export every record we hold for your tenant — clients, deals, invoices, campaigns, media, automations,
            loyalty members and more — as a single JSON file. This is your GDPR &ldquo;Right to Access&rdquo;.
          </p>
          <Button onClick={handleExport} disabled={exporting} className="bg-blue-600 hover:bg-blue-700" data-testid="gdpr-export-btn">
            {exporting ? (<><Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> Preparing export…</>) : (<><Download className="h-4 w-4 mr-1.5" /> Download my data (JSON)</>)}
          </Button>
        </CardContent>
      </Card>

      {/* Right to be Forgotten — soft-delete */}
      <Card className="border-red-300 bg-red-50/30">
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-heading flex items-center gap-2 text-red-700">
            <AlertTriangle className="h-4 w-4" />
            Delete My Tenant Data
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-sm text-gray-medium">
            Permanently remove your business data from Konnect. We keep it for <b>30 days</b> after you request deletion so
            you (or Platform Support) can undo an accidental request. After 30 days it is unrecoverable.
          </p>

          {hasPending ? (
            <div className="rounded-lg border border-red-300 bg-white p-4 space-y-3" data-testid="gdpr-pending-panel">
              <div className="flex items-start gap-2">
                <Shield className="h-4 w-4 text-red-600 mt-0.5 flex-shrink-0" />
                <div className="text-sm">
                  <p className="font-semibold text-red-700">Deletion scheduled</p>
                  <p className="text-gray-700 mt-1">
                    Your tenant data will be permanently deleted on{' '}
                    <span className="font-mono">{status.purge_at ? new Date(status.purge_at).toLocaleDateString() : '—'}</span>
                    {status.days_remaining !== null && <> &nbsp;·&nbsp; <span className="font-semibold">{status.days_remaining} days remaining</span></>}.
                  </p>
                  {status.requested_by && <p className="text-xs text-gray-500 mt-1">Requested by {status.requested_by}</p>}
                </div>
              </div>
              <Button
                variant="outline"
                onClick={handleCancelDeletion}
                disabled={cancelling}
                className="border-emerald-400 text-emerald-700 hover:bg-emerald-50"
                data-testid="gdpr-cancel-deletion-btn"
              >
                {cancelling ? (<><Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> Cancelling…</>) : (<><XCircle className="h-4 w-4 mr-1.5" /> Cancel deletion request</>)}
              </Button>
            </div>
          ) : (
            <Button variant="outline" onClick={() => setConfirmOpen(true)} className="border-red-400 text-red-700 hover:bg-red-100" data-testid="gdpr-delete-btn">
              <AlertTriangle className="h-4 w-4 mr-1.5" /> Request account deletion
            </Button>
          )}
        </CardContent>
      </Card>

      {/* Confirmation dialog */}
      <Dialog open={confirmOpen} onOpenChange={(o) => { setConfirmOpen(o); if (!o) { setConfirmText(''); setReason(''); } }}>
        <DialogContent className="sm:max-w-md" data-testid="gdpr-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-700">
              <AlertTriangle className="h-5 w-5" /> Confirm deletion request
            </DialogTitle>
            <DialogDescription>
              This will schedule your tenant data for permanent deletion in 30 days. You can cancel any time before then.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            <div>
              <Label className="text-xs">Type your business name to confirm</Label>
              <p className="text-[11px] text-gray-500 mb-1">Expected: <span className="font-mono text-gray-700">{businessName}</span></p>
              <Input
                value={confirmText}
                onChange={e => setConfirmText(e.target.value)}
                placeholder={businessName}
                data-testid="gdpr-confirm-input"
              />
            </div>
            <div>
              <Label className="text-xs">Reason (optional)</Label>
              <Textarea
                value={reason}
                onChange={e => setReason(e.target.value)}
                rows={2}
                placeholder="Help us improve — why are you leaving?"
                data-testid="gdpr-reason-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmOpen(false)}>Keep my data</Button>
            <Button
              onClick={handleScheduleDelete}
              disabled={scheduling || !confirmText.trim()}
              className="bg-red-600 hover:bg-red-700"
              data-testid="gdpr-confirm-delete-btn"
            >
              {scheduling ? (<><Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> Scheduling…</>) : 'Schedule deletion in 30 days'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
